import clsx from "clsx";
import { BiBath } from "react-icons/bi";
import { IoBedOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { HiArrowsPointingOut } from "react-icons/hi2";
import { formatPrice } from "../../utils/formatPrice";
import { useStatus } from "../common/hooks/useStatus";
import { formatDate } from "../../utils/dateFormatter";
import { useMapStore } from "../../stores/mapCordinations";
import { formatPropertyType } from "../../utils/formatPropertyType";
import { convertToFormattedAmount } from "../../utils/convertAmount";
import { useShowLoginModalStore } from "../../stores/loginModalStore";
import { useAuth } from "../../context/authProvider";
import {
  LastStatusEnum,
  type ListingCard,
} from "../../services/exploreListings";

interface ListCardProps {
  property: ListingCard;
}
const PropertyCard = ({ property }: ListCardProps) => {
  const truncateAddress = (address: string, maxLength: number) => {
    if (address.length > maxLength) {
      return `${address.slice(0, maxLength)}...`;
    }
    return address;
  };
  const { showProperty, setShowProperty, setHandleNavigate, handleNavigate } =
    useMapStore();
  const { authenticated } = useAuth();

  const status = useStatus(property?.lastStatus);
  const navigate = useNavigate();
  const { setShowLoginModal } = useShowLoginModalStore();

  const isSold = [
    LastStatusEnum.Sold,
    LastStatusEnum.SoldConditionally,
    LastStatusEnum.SoldConditionallyWithEscapeClause,
  ].includes(property?.lastStatus);

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
    <div
      style={{ width: "100%" }}
      className="flex overflow-hidden cursor-pointer border-[1px] border-[rgba(0,0,0,0.16)] flex-col rounded-[15px] bg-white bg-opacity-0 shadow-md"
      onClick={() => {
        isSold ? undefined : navigate(`/listings/${property?.listingId}`);
      }}
    >
      <div className="flex flex-col w-full font-semibold text-red-500 rounded-t-[15px]">
        <div
          className={clsx(
            "flex overflow-hidden relative flex-col w-full",
            !isSold ? "h-[230px]" : "h-[262px]"
          )}
        >
          <div
            className={clsx(
              "w-full relative",
              !isSold ? "h-[230px] min-h-[230px]" : "h-[262px] min-h-[262px]"
            )}
          >
            <img
              // onClick={() => navigate(`/listings/${listingId}`)}
              loading="lazy"
              src={property.displayImageUrl}
              alt="Property"
              className="object-cover absolute inset-0 size-full cursor-pointer"
            />
            {/* <div className="absolute bottom-3 right-4 h-8 w-8 rounded-full bg-[rgba(103,100,115,0.27)] flex items-center justify-center">
							<HeartIcon stroke="trasparent" fill="white" />
						</div> */}
            <div className="absolute w-full flex items-center justify-between bottom-3 left-0 px-4 h-[28px]">
              <div
                className={clsx(
                  "rounded-[6px] px-2 h-full bg-white font-semibold text-sm text-center flex items-center",
                  isSold ? "text-[#FF5757]" : "text-[#368014]"
                )}
              >
                {property.lastStatus && (
                  <div className="font-bold">{status.state}</div>
                )}
                {property.soldDate && (
                  <>
                    <div
                      className={clsx(
                        "h-3 w-px mx-3",
                        isSold ? "bg-[#FF5757]" : "bg-[#368014]"
                      )}
                    />
                    <div className="font-normal">
                      {!isSold
                        ? property.listDate
                          ? formatDate(property.listDate)
                          : "Unknown date"
                        : property.soldDate
                          ? formatDate(property.soldDate)
                          : "Unknown date"}
                    </div>
                  </>
                )}
              </div>
              {/* {!similarListings && (
							<div className="px-2 rounded-[6px] text-[#0A9D57] bg-[#D5EEE3] font-semibold text-xs h-full flex items-center justify-center">
								Above Asking
							</div>
						)} */}
            </div>
          </div>
          {property.propertyType && (
            <div className="absolute top-3 left-3 bg-[#368014] text-xs font-medium rounded-md text-white py-[6px] px-[10px]">
              {formatPropertyType(property.propertyType)}
            </div>
          )}
        </div>
        {!isSold && (
          <div className="bg-[#368014] px-3 h-[32px] w-full flex items-center justify-between text-white">
            <div className="text-sm font-semibold">Down Payment Boost</div>
            <div className="text-base font-bold">
              {`${property.downPaymentBoost
                ? `$${formatPrice(
                  convertToFormattedAmount(property.downPaymentBoost)
                )}`
                : "N/A"
                }`}
            </div>
          </div>
        )}
      </div>
      <div className="w-full pt-3 flex flex-col justify-between h-full">
        <div className="px-3">
          <div className="w-full relative text-xs text-black flex items-center justify-between mb-1">
            {!isSold && (
              <div className="text-[20px] text-black font-bold flex items-center">
                {property.listPrice
                  ? `$${formatPrice(
                    convertToFormattedAmount(property.listPrice)
                  )}`
                  : "N/A"}
              </div>
            )}
            <div
              className={clsx(
                "flex items-center gap-2",
                isSold ? "text-[#FF5757]" : "text-[#368014]"
              )}
            >
              {(property.listPrice || property.soldPrice) && (
                <div className="text-[20px] font-bold">
                  {!isSold
                    ? `$${formatPrice(
                      convertToFormattedAmount(
                        (property.minDownPayment ?? 0) -
                        (property.downPaymentBoost ?? 0)
                      )
                    )}`
                    : property.soldPrice
                      ? `$${formatPrice(
                        convertToFormattedAmount(property.soldPrice)
                      )}`
                      : "N/A"}
                </div>
              )}
              <div
                className={clsx(
                  "line-through text-base font-light pt-1",
                  isSold && "text-z-shark"
                )}
              >
                $
                {isSold
                  ? property?.listPrice
                    ? formatPrice(convertToFormattedAmount(property.listPrice))
                    : "N/A"
                  : property?.minDownPayment
                    ? formatPrice(
                      convertToFormattedAmount(property.minDownPayment)
                    )
                    : "N/A"}
              </div>
            </div>
          </div>
        </div>
        <div className="flex px-3 items-center justify-between">
          {property?.displayAddress && (
            <div className="text-sm text-z-shark my-1">
              {truncateAddress(property?.displayAddress, !isSold ? 15 : 300)}
            </div>
          )}
          {!isSold && (
            <div className="text-[13px] text-z-shark">Min. Down Payment</div>
          )}
        </div>
        <div className="text-sm h-[42px] flex items-center justify-between border-t border-[#ECECEC] mt-2">
          <div className="px-3 flex items-center w-full">
            <div className="flex items-center gap-1 justify-center text-nowrap flex-1">
              <IoBedOutline className="text-[#C8C8C8] text-xl" />
              <div>{property?.numBedrooms ? property?.numBedrooms : "N/A"}</div>
            </div>
            <div className="w-px h-8 bg-[#ECECEC] mx-2" />
            <div className="flex items-center gap-1 justify-center text-nowrap flex-1">
              <BiBath className="text-[#C8C8C8] text-xl" />
              <div>
                {property?.numBathrooms ? property?.numBathrooms : "N/A"}
              </div>
            </div>
            <div className="w-px h-8 bg-[#ECECEC] mx-2" />
            <div className="flex items-center gap-1 justify-center text-nowrap flex-1">
              <HiArrowsPointingOut className="text-[#C8C8C8] text-xl" />
              <div>
                {property?.sqft ? formatPrice(property?.sqft) : "N/A"} ft
              </div>
            </div>
          </div>
        </div>
      </div>
      {!authenticated && isSold && (
        <div
          className="absolute inset-0 bg-black bg-opacity-5 backdrop-blur-sm flex items-center justify-center z-30 rounded-[14px] border-2 border-black"
          onClick={() => {
            setShowLoginModal(true);
          }}
          onKeyDown={() => {
            setShowLoginModal(true);
          }}
        >
          <div className="bg-white p-6 rounded-[8px] shadow-lg w-[80%] max-w-md flex flex-col items-center justify-center text-center space-y-3">
            <div className="bg-[#FF5758] text-white rounded px-2 py-1 font-medium text-xs">
              SOLD
            </div>
            <div className="text-[#32302F]/80 max-w-[140px]">
              Login to View Sold Properties
            </div>
            <button
              type="button"
              className="bg-z-dark-moss-green rounded-full text-white w-full py-1.5"
            >
              Login
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export { PropertyCard };
